import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  PhoneCall, ShieldCheck, DollarSign, Send, Loader2, ArrowLeft,
  CheckCircle2, Clock, AlertTriangle, Hash, MessageCircle,
  Sparkles, TrendingUp, Award, RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  getPublicSettings, submitPersonalCallRequest, checkPersonalCallStatus, apiErrMsg,
} from '../lib/api';

const LS_KEY = 'alvee_personal_call_last_trader_id';
const MIN_DEPOSIT_USD = 500;

function StatusBadge({ status }) {
  if (status === 'approved') {
    return (
      <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(70,200,120,0.55)] bg-[rgba(70,200,120,0.08)] text-[hsl(140_65%_72%)] gap-1">
        <CheckCircle2 className="h-3 w-3" /> Approved · Signal Provider notified
      </Badge>
    );
  }
  if (status === 'called') {
    return (
      <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(199,180,80,0.6)] bg-[rgba(255,220,80,0.06)] text-[hsl(43_92%_78%)] gap-1">
        <PhoneCall className="h-3 w-3" /> Analyst is calling you now
      </Badge>
    );
  }
  if (status === 'completed') {
    return (
      <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(140,90,255,0.6)] bg-[rgba(140,90,255,0.08)] text-[hsl(280_70%_80%)] gap-1">
        <Award className="h-3 w-3" /> Call completed
      </Badge>
    );
  }
  if (status === 'rejected') {
    return (
      <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(225,29,46,0.55)] bg-[rgba(225,29,46,0.08)] text-[hsl(0_72%_72%)] gap-1">
        <AlertTriangle className="h-3 w-3" /> Rejected — see Telegram support
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(199,180,80,0.55)] bg-[rgba(255,220,80,0.05)] text-[hsl(43_92%_75%)] gap-1">
      <Clock className="h-3 w-3" /> Waiting for Quotex deposit verification
    </Badge>
  );
}

function BenefitPill({ Icon, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.06)] px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] text-[hsl(43_92%_72%)]">
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
  );
}

export default function PersonalCallPage() {
  const [settings, setSettings] = useState(null);
  const [traderId, setTraderId] = useState('');
  const [telegram, setTelegram] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { id, status, verified_deposit_usd, ... }
  const [checking, setChecking] = useState(false);

  // Load settings + attempt to restore last-known trader status.
  useEffect(() => {
    (async () => {
      try { setSettings(await getPublicSettings()); } catch { /* ignore */ }
      const savedTid = (typeof window !== 'undefined') ? localStorage.getItem(LS_KEY) : '';
      if (savedTid) {
        try {
          const res = await checkPersonalCallStatus(savedTid);
          if (res?.found && res.request) setResult(res.request);
        } catch { /* soft-fail */ }
      }
    })();
  }, []);

  const brokerLink = settings?.broker_link || '#';

  const canSubmit = useMemo(
    () => traderId.trim().length >= 3 && telegram.trim().length >= 2 && !submitting,
    [traderId, telegram, submitting],
  );

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        quotex_trader_id: traderId.trim(),
        telegram_username: telegram.trim(),
        note: note.trim(),
      };
      const res = await submitPersonalCallRequest(payload);
      setResult(res);
      try { localStorage.setItem(LS_KEY, traderId.trim()); } catch { /* ignore */ }
      if (res.status === 'approved' || res.auto_approved) {
        toast.success('Approved! Our Signal Provider will call you on Telegram shortly.');
      } else {
        toast.info('Request received. Deposit >= $500 will auto-approve after Quotex postback.');
      }
    } catch (err) {
      toast.error(apiErrMsg(err, 'Failed to submit'));
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, traderId, telegram, note]);

  const refreshStatus = useCallback(async () => {
    const tid = (result?.quotex_trader_id || traderId).trim();
    if (!tid) return;
    setChecking(true);
    try {
      const res = await checkPersonalCallStatus(tid);
      if (res?.found && res.request) {
        setResult(res.request);
        toast.success('Status refreshed');
      } else {
        toast.info('No request found for this trader ID (last 7 days)');
      }
    } catch (err) {
      toast.error(apiErrMsg(err, 'Failed to refresh status'));
    } finally {
      setChecking(false);
    }
  }, [result, traderId]);

  const resetForm = () => {
    setResult(null);
    setTraderId('');
    setTelegram('');
    setNote('');
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
  };

  return (
    <main className="min-h-screen bg-[hsl(0_0%_4%)] text-[hsl(0_0%_98%)]">
      <Navbar settings={settings} />

      <section className="relative overflow-hidden noise-overlay">
        {/* Ambient gold glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
          style={{
            background:
              'radial-gradient(60% 55% at 50% 0%, rgba(245,185,66,0.15) 0%, rgba(140,90,255,0.06) 40%, rgba(0,0,0,0) 75%)',
          }}
        />

        <div className="container-x pt-8 pb-16 relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-white transition-colors"
            data-testid="personal-call-back-link"
          >
            <ArrowLeft className="h-3 w-3" /> Back to home
          </Link>

          <div className="mt-4 grid gap-8 lg:grid-cols-[1.15fr,1fr] items-start">
            {/* LEFT: pitch */}
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <span className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)]">
                  1-on-1 Personal Call
                </span>
                <span className="h-px w-10 bg-[hsl(43_92%_56%)]/60" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground inline-flex items-center gap-1.5">
                  <span className="pulse-dot" /> Elite depositors only
                </span>
              </div>

              <h1 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.94] tracking-tight">
                Talk 1-on-1 with a<br />
                <span className="bg-gradient-to-r from-[hsl(43_92%_60%)] via-[hsl(38_85%_58%)] to-[hsl(43_92%_60%)] bg-clip-text text-transparent">
                  Live Market Analyst
                </span>
              </h1>

              <p className="text-sm md:text-base text-[hsl(0_0%_82%)] max-w-xl leading-relaxed">
                Fund your Quotex account with{' '}
                <span className="font-mono text-white font-semibold">${MIN_DEPOSIT_USD}+</span>{' '}
                and unlock a private Telegram call from our Signal Provider. They walk you through
                setups, risk plan, and best-time trades — tailored to your account size.
              </p>

              <div className="flex flex-wrap gap-2 pt-1">
                <BenefitPill Icon={ShieldCheck} label="Quotex-verified" />
                <BenefitPill Icon={DollarSign} label={`Min $${MIN_DEPOSIT_USD} deposit`} />
                <BenefitPill Icon={PhoneCall} label="Direct Telegram call" />
                <BenefitPill Icon={Sparkles} label="Auto-approved via postback" />
              </div>

              {/* Steps */}
              <div className="mt-4 grid gap-3">
                {[
                  {
                    n: '01',
                    title: 'Deposit $500+ on Quotex',
                    body: 'Use our Quotex broker link (or your existing linked account). Any total deposit ≥ $500 within 7 days qualifies.',
                  },
                  {
                    n: '02',
                    title: 'Submit your Trader ID + Telegram',
                    body: 'Fill the short form on the right. We verify against real Quotex S2S postbacks — no manual checks, no waiting on humans.',
                  },
                  {
                    n: '03',
                    title: 'Signal Provider calls you',
                    body: 'Once approved, our next available Market Analyst picks up your request and messages you on Telegram to schedule the call.',
                  },
                ].map((s) => (
                  <div
                    key={s.n}
                    className="rounded-lg border border-white/10 bg-white/[0.02] p-3.5 flex gap-3"
                    data-testid={`pcall-step-${s.n}`}
                  >
                    <div className="font-display text-2xl text-[hsl(43_92%_56%)] tabular-nums leading-none">
                      {s.n}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{s.title}</div>
                      <div className="text-xs text-[hsl(0_0%_78%)] mt-0.5 leading-relaxed">{s.body}</div>
                    </div>
                  </div>
                ))}
              </div>

              {settings?.broker_enabled && settings?.broker_link && (
                <div className="rounded-lg border border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.06)] p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-[hsl(0_0%_88%)]">
                    <TrendingUp className="inline h-3.5 w-3.5 mr-1 text-[hsl(43_92%_60%)]" />
                    Don&apos;t have a Quotex account yet? Open one using our link — deposits get tracked
                    automatically for the personal-call queue.
                  </div>
                  <a href={brokerLink} target="_blank" rel="noreferrer" data-testid="pcall-broker-link">
                    <Button className="h-9 px-3 cta-sheen text-white border-0 text-xs">
                      Open Quotex
                    </Button>
                  </a>
                </div>
              )}
            </div>

            {/* RIGHT: form or status */}
            <div className="lg:sticky lg:top-24">
              <Card className="bg-[hsl(240_6%_7%)] border-white/10 overflow-hidden">
                <CardContent className="p-5 sm:p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <PhoneCall className="h-4 w-4 text-[hsl(43_92%_56%)]" />
                    <div className="font-display text-sm uppercase tracking-[0.18em]">
                      Request a personal call
                    </div>
                  </div>

                  {result ? (
                    <ResultPanel
                      result={result}
                      onRefresh={refreshStatus}
                      checking={checking}
                      onReset={resetForm}
                    />
                  ) : (
                    <form onSubmit={handleSubmit} className="grid gap-3" data-testid="personal-call-form">
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5">
                          <Hash className="h-3 w-3" /> Quotex Trader ID
                        </Label>
                        <Input
                          value={traderId}
                          onChange={(e) => setTraderId(e.target.value)}
                          placeholder="e.g. 12345678"
                          className="bg-white/[0.03] border-white/10 h-11 font-mono"
                          data-testid="pcall-trader-id"
                          required
                          minLength={3}
                          maxLength={80}
                        />
                        <div className="text-[10px] text-muted-foreground mt-1">
                          Find this in your Quotex profile (top-right avatar → account details).
                        </div>
                      </div>

                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5">
                          <MessageCircle className="h-3 w-3" /> Telegram username
                        </Label>
                        <Input
                          value={telegram}
                          onChange={(e) => setTelegram(e.target.value)}
                          placeholder="@yourhandle or +8801XXXXXXXXX"
                          className="bg-white/[0.03] border-white/10 h-11"
                          data-testid="pcall-telegram"
                          required
                          minLength={2}
                          maxLength={120}
                        />
                        <div className="text-[10px] text-muted-foreground mt-1">
                          Our Signal Provider will DM you here. Make sure DMs are open.
                        </div>
                      </div>

                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                          What do you want help with? (optional)
                        </Label>
                        <Textarea
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          rows={3}
                          placeholder="e.g. Risk management for a $700 account, best trading hours in BDT…"
                          className="bg-white/[0.03] border-white/10"
                          data-testid="pcall-note"
                          maxLength={400}
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={!canSubmit}
                        className="w-full h-12 bg-gradient-to-r from-[hsl(43_92%_56%)] to-[hsl(38_85%_50%)] hover:from-[hsl(43_92%_62%)] hover:to-[hsl(38_85%_56%)] text-black font-bold border-0 gap-2 disabled:opacity-60"
                        data-testid="pcall-submit"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Verifying deposit…
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" /> Request Personal Call
                          </>
                        )}
                      </Button>

                      <div className="text-[10px] text-muted-foreground leading-relaxed">
                        By submitting you agree to be contacted on Telegram. We verify eligibility
                        via Quotex S2S postbacks. Requests are limited to one active per Trader ID
                        every 7 days.
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>

              {/* Trust row under card */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-2">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Verified via</div>
                  <div className="text-[11px] font-semibold mt-0.5">Quotex Postback</div>
                </div>
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-2">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Min deposit</div>
                  <div className="text-[11px] font-semibold mt-0.5">${MIN_DEPOSIT_USD} USD</div>
                </div>
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-2">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Response</div>
                  <div className="text-[11px] font-semibold mt-0.5">Same day</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer settings={settings} />
    </main>
  );
}

function ResultPanel({ result, onRefresh, checking, onReset }) {
  const status = result?.status || 'pending_verify';
  const deposit = Number(result?.verified_deposit_usd || 0);
  const min = Number(result?.min_required_usd || MIN_DEPOSIT_USD);

  return (
    <div className="space-y-4" data-testid="pcall-result-panel">
      <div className="flex items-start gap-3">
        {status === 'approved' || status === 'called' || status === 'completed' ? (
          <CheckCircle2 className="h-8 w-8 text-[hsl(140_65%_65%)] shrink-0" />
        ) : status === 'rejected' ? (
          <AlertTriangle className="h-8 w-8 text-[hsl(0_72%_70%)] shrink-0" />
        ) : (
          <Clock className="h-8 w-8 text-[hsl(43_92%_60%)] shrink-0" />
        )}
        <div>
          <div className="font-display text-lg">
            {status === 'approved' && 'You’re in the queue!'}
            {status === 'called' && 'Analyst is calling you'}
            {status === 'completed' && 'Call completed — thank you!'}
            {status === 'rejected' && 'Request rejected'}
            {status === 'pending_verify' && 'Waiting on Quotex verification'}
          </div>
          <div className="mt-1"><StatusBadge status={status} /></div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-2.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Trader ID</div>
          <div className="font-mono text-white mt-0.5">{result.quotex_trader_id}</div>
        </div>
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-2.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Telegram</div>
          <div className="text-white mt-0.5 truncate">{result.telegram_username}</div>
        </div>
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-2.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Verified deposit</div>
          <div className="font-mono text-white mt-0.5">
            {deposit > 0 ? `$${deposit.toFixed(2)}` : '—'}
          </div>
        </div>
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-2.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Threshold</div>
          <div className="font-mono text-white mt-0.5">${min.toFixed(0)}</div>
        </div>
      </div>

      {status === 'pending_verify' && (
        <div className="rounded-md border border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.06)] p-3 text-xs text-[hsl(0_0%_88%)]">
          Your Quotex deposit hasn&apos;t cleared the ${min.toFixed(0)} threshold yet. As soon as it does,
          the next incoming Quotex postback will auto-approve this request. You can also refresh
          manually below.
        </div>
      )}

      {status === 'approved' && (
        <div className="rounded-md border border-[rgba(70,200,120,0.35)] bg-[rgba(70,200,120,0.06)] p-3 text-xs text-[hsl(0_0%_88%)]">
          A Signal Provider will DM you on Telegram (
          <span className="font-mono text-white">{result.telegram_username}</span>) shortly.
          Keep DMs open — usually within the same trading session.
        </div>
      )}

      {status === 'called' && result.called_by_staff_name && (
        <div className="rounded-md border border-[rgba(255,220,80,0.35)] bg-[rgba(255,220,80,0.06)] p-3 text-xs text-[hsl(0_0%_88%)]">
          <span className="text-[hsl(43_92%_75%)] font-semibold">{result.called_by_staff_name}</span> is
          reaching out now. Please respond on Telegram to schedule.
        </div>
      )}

      {status === 'rejected' && (
        <div className="rounded-md border border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.06)] p-3 text-xs text-[hsl(0_0%_88%)]">
          Please contact Telegram support for a manual review.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onRefresh}
          disabled={checking}
          className="flex-1 h-10 border-white/10 bg-white/[0.02] gap-2"
          data-testid="pcall-refresh"
        >
          {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh status
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onReset}
          className="flex-1 h-10 border-white/10 bg-white/[0.02] gap-2"
          data-testid="pcall-new-request"
        >
          New request
        </Button>
      </div>
    </div>
  );
}
